import { RootState } from "@base/store";
import Button from "@components/atoms/button";
import { gameDetailsActions } from "@components/features/game-details/gameDetails.slice";
import { PlayerProps as GamePlayerProps } from "@components/features/game/game.type";
import { reviewActions } from "@components/features/review/review.slice";
import { ReviewProps } from "@components/features/review/review.type";
import Rating from "@components/molecules/rating";
import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { ConnectedProps, connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Avatar,
  Container,
  Content,
  Header,
  OptionWithDividerWrapper,
  OptionWithIconWrapper,
  PlayerInfoWrapper,
  PlayerName,
  PlayerWrapper,
  PlayersContainer,
  Skip,
  TeamDescription,
  Title,
} from "./userReview.styled";
import { ComponentProps } from "./userReview.type";

import { ReactComponent as Back } from "@assets/images/back/chevron-left.svg";

type PlayerProps = GamePlayerProps & {
  rating: number | null;
};

type ReduxProps = ConnectedProps<typeof connector>;

const UserReview = ({
  submitReview,
  handelNext,
  handleBack,
}: ReduxProps & ComponentProps) => {
  const navigate = useNavigate();
  const selectedGame = useSelector((state: RootState) => state.selectedGame);
  const user = useSelector((state: RootState) => state.user.user);

  // Initialize player ratings with default values
  const initialPlayerRatings: PlayerProps[] = [
    ...selectedGame?.sides[0].players,
    ...selectedGame?.sides[1].players,
  ].map((player) => ({ ...player, rating: 0 }));
  const [players, setPlayers] = useState<PlayerProps[]>(initialPlayerRatings);

  const getPlayerIdByUUID = (uuid: string) => {
    for (const side of selectedGame?.sides) {
      for (const player of side.players) {
        if (player.rsvp.user.id === uuid) {
          return player.id;
        }
      }
    }
  };

  const handleReviewList = async () => {
    if (user.uuid) {
      let reviewList: ReviewProps[] = [];
      let body = {
        reviewer_id: getPlayerIdByUUID(user.uuid!)!,
        reviews: reviewList,
      };
      players.forEach((player) => {
        reviewList.push({
          rating: player?.rating,
          review_text: "",
          player_id: player?.id,
        });
      });
      const response = await submitReview(body);
      if (response?.meta?.requestStatus === "fulfilled") {
        navigate("/home");
      }
    }
  };

  const userViewRender = () => {
    const stringAvatar = (name: string) => {
      return {
        children: `${name?.split(" ")[0][0]?.toUpperCase()}${name
          ?.split(" ")[1][0]
          ?.toUpperCase()}`,
      };
    };

    return (
      <>
        {players.length > 0 ? (
          <PlayersContainer>
            {players.map((player, index) => (
              <PlayerInfoWrapper key={index}>
                <OptionWithDividerWrapper>
                  <OptionWithIconWrapper>
                    <PlayerWrapper>
                      <Avatar
                        alt="user Photo"
                        {...stringAvatar(
                          `${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`
                        )}
                      />
                      <PlayerName>{`${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`}</PlayerName>
                    </PlayerWrapper>
                    <Rating
                      value={player.rating} // Use the rating value from each player
                      handleChange={(e, newRating) =>
                        handlePlayerRatingChange(index, newRating)
                      }
                    />
                  </OptionWithIconWrapper>
                </OptionWithDividerWrapper>
              </PlayerInfoWrapper>
            ))}
          </PlayersContainer>
        ) : (
          <TeamDescription>
            {
              "No one in this team yet. Invite more players to see more people in this list."
            }
          </TeamDescription>
        )}
        <Box
          sx={{
            flex: "1",
            display: "flex",
            alignItems: "flex-end",
            paddingBottom: "16px",
          }}
        >
          <Button
            textColor={"#FFF"}
            text={"Submit"}
            type={"button"}
            onClick={handleReviewList}
          />
        </Box>
      </>
    );
  };

  const handlePlayerRatingChange = (
    playerIndex: number,
    newRating: number | null
  ) => {
    const updatedPlayers = [...players];
    updatedPlayers[playerIndex].rating = newRating;
    setPlayers(updatedPlayers);
  };

  return (
    <Container>
      <Header>
        <Back onClick={handleBack} />
        <Skip onClick={handelNext}>Skip</Skip>
      </Header>
      <Content>
        <Title>Leave reviews for your mates</Title>
        {userViewRender()}
      </Content>
    </Container>
  );
};

const mapDispatchToProps = {
  reset: gameDetailsActions.reset,
  submitReview: reviewActions.reviewPlayersList,
};

const connector = connect(undefined, mapDispatchToProps);
const UserReviewRedux = connector(UserReview);

export { UserReviewRedux as UserReview };
